import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { AppDispatch, RootState } from '../../../store/store';
import BackButton from '../../../components/Common/BackButton';
import Loader from '../../../components/Common/Loader';
import { LogInSchema } from '../../Builder/services/BuilderValidationSchemes';
import { handleGoogleLogin } from '../../Builder/services/google-login.service';
import { fetchUserDetails, signInUser } from '../../../store/thunks/user.thunk';
import { setRedirectURL } from '../../../store/userSlice';

const GeneralLogin = () => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [googleLoader, setGoogleLoader] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const navigateTo = useNavigate();
  const isLoading = useSelector((state: RootState) => state.user.isSending);
  const userAuthorized = useSelector((state: RootState) => state.user.userAuthorized);
  const user = useSelector((state: RootState) => state.user.user);

  useEffect(() => {
    if (user && userAuthorized) {
      dispatch(setRedirectURL('/'));
    }
  }, [dispatch, user, userAuthorized]);

  const handleSignInFormSubmit = async (values: any) => {
    dispatch(signInUser({ ...values, dispatch, navigateTo }));
  };

  return (
    <div className="min-h-screen bg-whiteGrayBackground font-sofiaSans flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm py-8 px-4 sm:px-11">
        <div className="text-left mb-8">
          <h1 className="text-3xl font-bold text-black leading-tight">Bring Your Story to Life!</h1>
        </div>

        <div className="mb-4">
          <button
            disabled={googleLoader}
            onClick={() =>
              handleGoogleLogin(
                setGoogleLoader,
                (email: string) =>
                  dispatch(
                    fetchUserDetails({
                      email,
                      validate: () => {
                        return { isValid: true };
                      },
                    }),
                  ),
                navigateTo,
                dispatch,
              )
            }
            className="w-full flex justify-center gap-3 bg-secondaryStoryOptionalLightBlueText text-white font-bold font-sofiaSans rounded-lg px-4 py-2 text-base items-center cursor-pointer border border-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FontAwesomeIcon title="Login with Google" icon={faGoogle} className="text-xl text-white cursor-pointer" />
            Continue with Google
            <Loader loader={googleLoader} />
          </button>
        </div>

        <div className="relative py-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t-2 border-secondaryBorderBackgroundLightGreyColor" />
          </div>
          <div className="relative flex justify-center text-base">
            <span className="px-2 bg-whiteGrayBackground text-secondaryStoryBlackText">Or</span>
          </div>
        </div>

        <div className="">
          <Formik
            initialValues={{
              email: '',
              password: '',
            }}
            validationSchema={LogInSchema}
            onSubmit={(values) => {
              handleSignInFormSubmit(values);
            }}
          >
            {({ errors, touched, values, handleChange, handleSubmit }) => (
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                  />
                  {touched.email && errors.email && (
                    <div className="absolute text-storyseconderror text-sm">{errors.email}</div>
                  )}
                </div>

                <div className="relative">
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={values.password}
                      onChange={handleChange}
                      placeholder="Password"
                      className="appearance-none block w-full px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackText"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-4 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEye : faEyeSlash}
                        className="text-gray-400 hover:text-gray-600 w-5 h-5"
                      />
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <div className="absolute text-storyseconderror text-sm">{errors.password}</div>
                  )}
                </div>

                <div className="text-left">
                  <Link
                    to="/forgot-password"
                    className="text-secondaryStoryAdditionalLightBlueText hover:underline text-base"
                  >
                    Forgot your Password?
                  </Link>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-base font-bold text-white bg-secondaryStoryAdditionalLightBlueText hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Log in
                    <Loader loader={isLoading} />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <BackButton onClick={() => window.history.back()}></BackButton>
                  </div>
                  <Link
                    to="/auth"
                    className="text-secondaryStoryAdditionalLightBlueText hover:underline text-base font-bold"
                  >
                    Sign Up
                  </Link>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default GeneralLogin;
